import { toast } from 'sonner'
import { logger } from 'v2/utils/logger'
import { ToastContent } from '../components/persistent-sonner-handler/sonner-content'

const STORAGE_KEY_PREFIX = 'sonner-'
export interface SonnerConfig {
  id: string
  title: string
  description: string
  storageKey?: string
  showDoNotShowAgain?: boolean
  showCloseButton?: boolean
  type?: 'success' | 'info' | 'warning' | 'error' | 'loading' | 'message'
  action?: {
    label: string
    onClick: () => Promise<void>
  }
  onClose?: () => void
}

class SonnerManager {
  private static instance: SonnerManager

  private toastIds: Record<string, string | number> = {}

  private checkboxState: Record<string, boolean> = {}

  private constructor() {
    // Private constructor to enforce singleton
  }

  public static getInstance(): SonnerManager {
    if (!SonnerManager.instance) {
      SonnerManager.instance = new SonnerManager()
    }
    return SonnerManager.instance
  }

  private clearToast(id: string) {
    delete this.toastIds[id]
    delete this.checkboxState[id]
  }

  public showToast = (config: SonnerConfig, shouldShow: boolean) => {
    const {
      id,
      title,
      description,
      storageKey,
      showDoNotShowAgain,
      action,
      type = 'warning',
    } = config
    const prefixedStorageKey = `${STORAGE_KEY_PREFIX}${storageKey || id}`

    // Check if user has chosen to not show this toast
    if (showDoNotShowAgain && localStorage.getItem(prefixedStorageKey) === 'true') return

    // If a toast of this type is already active, don't show another one
    if (shouldShow && !this.toastIds[id]) {
      logger.log('showing toast', { id })
      let toastId: string | number | undefined
      const content = (
        <ToastContent
          showCloseButton={config.showCloseButton}
          description={description}
          id={id}
          onCheckboxChange={(checked) => {
            this.checkboxState[id] = checked
          }}
          showDoNotShowAgain={showDoNotShowAgain}
          onClose={() => {
            if (showDoNotShowAgain && this.checkboxState[id]) {
              localStorage.setItem(prefixedStorageKey, 'true')
            }
            toast.dismiss(toastId)
            this.clearToast(id)
            config.onClose?.()
          }}
        />
      )

      toastId = toast[type](title, {
        description: content,
        duration: Infinity,
        position: 'bottom-right',
        onDismiss: () => this.clearToast(id),
        id,
        ...(action && {
          action: {
            label: action.label,
            onClick: async () => {
              await action.onClick()
              this.clearToast(id)
            },
          },
        }),
      })
      this.toastIds[id] = toastId
    } else if (!shouldShow && this.toastIds[id]) {
      toast.dismiss(this.toastIds[id])
      this.clearToast(id)
    }
    return this.toastIds[id]
  }

  public dismissAll() {
    Object.values(this.toastIds).forEach((id) => toast.dismiss(id))
    this.toastIds = {}
    this.checkboxState = {}
  }

  public dismiss(id?: string | number) {
    if (id) {
      toast.dismiss(this.toastIds[id])
      this.clearToast(id as string)
    }
  }
}

export const sonnerManager = SonnerManager.getInstance()
